import { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, FileSpreadsheet, Loader2, UploadCloud } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errorMessage } from "../../lib/api";
import { plural } from "../../lib/format";
import { ImportReviewPanel } from "./ImportReviewPanel";
import { ChangeList, ImportHistory } from "./ImportHistory";

const MSG_EXT = "Sube un archivo Excel (.xlsx)";
const MSG_CUT = "Indica la fecha de corte (dd/mm/aaaa)";
const MONTHS = { jan: 1, ene: 1, feb: 2, mar: 3, apr: 4, abr: 4, may: 5, jun: 6, jul: 7, aug: 8, ago: 8, sep: 9, oct: 10, nov: 11, dec: 12, dic: 12 };
const pad = (n) => String(n).padStart(2, "0");

export function cutFromName(name) {
  const re = /(\d{1,2})[\s._-]*([a-z]{3,10})\.?[\s._-]*(\d{4})/gi;
  for (const m of name.matchAll(re)) {
    const month = MONTHS[m[2].slice(0, 3).toLowerCase()];
    if (month && +m[1] >= 1 && +m[1] <= 31) return `${pad(m[1])}/${pad(month)}/${m[3]}`;
  }
  return "";
}

function cutToIso(text) {
  const named = cutFromName(text.trim());
  const m = (named || text.trim()).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(Date.UTC(+m[3], +m[2] - 1, +m[1]));
  if (d.getUTCDate() !== +m[1] || d.getUTCMonth() !== +m[2] - 1) return null;
  return `${m[3]}-${pad(m[2])}-${pad(m[1])}`;
}

const isoToText = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const PAYMENT_SHEET = "5. Actual Payment data";
const MSG_NO_PAYMENTS = "Este archivo no trae hoja de pagos; se conservan los saldos actuales.";

async function hasPaymentSheet(file) {
  try {
    const { BlobReader, TextWriter, ZipReader } = await import("@zip.js/zip.js");
    const reader = new ZipReader(new BlobReader(file));
    const wb = (await reader.getEntries()).find((e) => e.filename === "xl/workbook.xml");
    const xml = wb ? await wb.getData(new TextWriter()) : "";
    await reader.close();
    return !wb || xml.includes(`name="${PAYMENT_SHEET}"`);
  } catch {
    return true;
  }
}
const ITEMS = [
  ["clientes_nuevos", "Clientes nuevos"],
  ["clientes_actualizados", "Clientes actualizados"],
  ["creditos_nuevos", "Créditos nuevos"],
  ["creditos_actualizados", "Créditos actualizados"],
];

const ARR = [["vinculado", "Clientes vinculados"], ["cambio_id", "Clientes con ID nuevo"], ["serie_9000", "Clientes a numeración 9000"],
  ["bandeja", "Por clasificar → clientes"], ["reacomodo", "Archivos reacomodados"], ["propuesta_ia", "Propuestas de IA resueltas"],
  ["cliente_no_real", "Clientes no reales eliminados"]];

const Summary = ({ s }) => (
  <div className="space-y-3" data-testid="import-summary">
    <div className="grid grid-cols-2 gap-2.5">
      {ITEMS.map(([k, label]) => (
        <div key={k} className="rounded-[12px] border border-[#e8edf5] bg-[#f8fafd] px-3 py-2.5">
          <div className="text-xl font-extrabold text-[#182235]" data-testid={`summary-${k.replace(/_/g, "-")}`}>{s[k]}</div>
          <div className="text-xs font-semibold text-[#5b667a]">{label}</div>
        </div>
      ))}
    </div>
    <div className="rounded-[12px] bg-[rgba(19,182,122,0.12)] px-3 py-2.5 text-sm font-semibold text-[#0f8b5f]" data-testid="summary-sin-cambios">
      Sin cambios: {plural(s.sin_cambios_clientes, "cliente", "clientes")} · {plural(s.sin_cambios_creditos, "crédito", "créditos")}
    </div>
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-[#182235] sm:grid-cols-3" data-testid="summary-arreglo">
      {ARR.map(([k, label]) => (
        <div key={k}><span className="font-extrabold" data-testid={`summary-arreglo-${k}`}>{s.arreglo?.[k] || 0}</span> {label}</div>
      ))}
    </div>
    <div className="text-sm font-semibold text-[#182235]" data-testid="summary-total-clientes">Total: {plural(s.total_clientes, "cliente", "clientes")} · {plural(s.total_creditos, "crédito", "créditos")}</div>
    <div className="text-sm text-[#3b475c]" data-testid="summary-pagos">
      {s.hoja_pagos
        ? `Pagos actualizados: ${plural(s.creditos_con_pagos, "crédito", "créditos")} · corte al ${isoToText(s.fecha_corte)}`
        : "El archivo no tiene hoja de pagos; se conservaron los saldos y fechas de corte."}
    </div>
    {s.cambios?.length > 0 && (
      <details className="rounded-[12px] border border-[#e8edf5] px-3 py-2" data-testid="summary-cambios">
        <summary className="cursor-pointer text-sm font-bold text-[#182235]">Historial de este lote ({s.cambios.length} cambios)</summary>
        <ChangeList changes={s.cambios} testId="summary-cambios-list" />
      </details>
    )}
    <p className="text-xs text-[#5b667a]">No se borró ningún crédito ni documento. Los archivos se movieron solo como referencia; no se copió ni se volvió a subir ninguno.</p>
  </div>
);

const JobProgress = ({ job }) => (
  <div className="space-y-2 rounded-[12px] border border-[#e8edf5] bg-[#f8fafd] px-3 py-3" data-testid="import-job-progress">
    <div className="flex items-center justify-between text-sm font-semibold text-[#182235]">
      <span className="flex items-center gap-2" data-testid="import-job-step"><Loader2 className="h-4 w-4 animate-spin text-[#0f5ef7]" /> {job.kind === "aplicar" ? "Aplicando" : "Revisando"}: {job.step_label}…</span>
      <span data-testid="import-job-percent">{job.progress}%</span>
    </div>
    <div className="h-2 overflow-hidden rounded-full bg-[#e8edf5]"><div className="h-full rounded-full bg-[#0f5ef7] transition-[width] duration-500" style={{ width: `${job.progress}%` }} /></div>
    <p className="text-[11px] text-[#5b667a]" data-testid="import-job-detail">{job.detail ? `${job.detail} · ` : ""}{job.updated_at ? `última actualización ${new Date(job.updated_at).toLocaleTimeString("es-MX")}` : ""}</p>
    <p className="text-[11px] text-[#5b667a]">Puedes cerrar esta ventana o recargar la página; el proceso sigue en el servidor. {job.kind === "aplicar" ? "Si algo falla, no se cambia nada." : "Nada se guarda hasta pulsar «Aplicar»."}</p>
  </div>
);

const NO_OFF = () => ({ parciales: new Set(), reacomodar: new Set(), no_reales: new Set() });

export const ImportExcelDialog = ({ onImported }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [cut, setCut] = useState("");
  const [payments, setPayments] = useState(null);
  const [review, setReview] = useState(null);
  const [off, setOff] = useState(NO_OFF);
  const [job, setJob] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const prevStatus = useRef(null);
  const epoch = useRef(0);
  const busy = job?.status === "en_curso";

  const applyJob = useCallback((j) => {
    const was = prevStatus.current;
    prevStatus.current = j ? `${j.id}:${j.status}` : null;
    setJob(j);
    if (!j) return;
    if (j.status === "error") setError(j.error || "No se pudo revisar el archivo");
    if (j.status !== "terminado") return;
    if (j.kind === "revisar") { setReview(j.result); setReviewId(j.id); setSummary(null); }
    else {
      setReview(null); setSummary(j.result);
      if (was === `${j.id}:en_curso`) onImported();
    }
  }, [onImported]);

  useEffect(() => {
    if (!open) return;
    const asked = epoch.current;
    // only a RUNNING job is restored, and never over a file the user picked meanwhile
    api.get("/import/jobs/current").then((r) => {
      const j = r.data.job;
      if (asked === epoch.current && j?.status === "en_curso") applyJob(j);
    }).catch(() => {});
  }, [open, applyJob]);

  useEffect(() => {
    if (!open || !busy) return undefined;
    const t = setInterval(() => api.get(`/import/jobs/${job.id}`).then((r) => applyJob(r.data)).catch(() => {}), 1500);
    return () => clearInterval(t);
  }, [open, busy, job?.id, applyJob]);

  const toggle = (group, key, on) => setOff((o) => {
    const next = new Set(o[group]);
    if (on) next.delete(key); else next.add(key);
    return { ...o, [group]: next };
  });

  const dismiss = (j) => (j && j.status !== "en_curso" ? api.post(`/import/jobs/${j.id}/dismiss`).catch(() => {}) : null);

  const reset = (o) => {
    setOpen(o);
    if (!o) {
      epoch.current += 1;
      dismiss(job);
      setFile(null); setError(""); setSummary(null); setCut(""); setPayments(null); setReview(null); setOff(NO_OFF()); setJob(null); setReviewId(null);
    }
  };

  const cancelReview = () => { dismiss(job); setReview(null); setJob(null); setReviewId(null); };

  const pick = (f) => {
    epoch.current += 1;
    if (job && job.status !== "en_curso") { dismiss(job); setJob(null); prevStatus.current = null; }
    setReviewId(null);
    setSummary(null);
    setReview(null);
    setOff(NO_OFF());
    setFile(f || null);
    setPayments(null);
    if (f) setCut(cutFromName(f.name));
    const bad = f && !f.name.toLowerCase().endsWith(".xlsx");
    setError(bad ? MSG_EXT : "");
    if (f && !bad) hasPaymentSheet(f).then(setPayments);
  };

  const start = async (form, fallback) => {
    setError("");
    try {
      applyJob((await api.post("/import/jobs", form, { timeout: 120000 })).data);
    } catch (err) {
      setError(errorMessage(err, fallback));
    }
  };

  const sendReview = () => {
    if (!file || !file.name.toLowerCase().endsWith(".xlsx")) return setError(MSG_EXT);
    const iso = payments ? cutToIso(cut) : null;
    if (payments && !iso) return setError(MSG_CUT);
    const form = new FormData();
    form.append("kind", "revisar");
    form.append("file", file);
    if (iso) form.append("cut_date", iso);
    return start(form, "No se pudo revisar el archivo");
  };

  const sendApply = () => {
    const form = new FormData();
    form.append("kind", "aplicar");
    form.append("review_id", reviewId);
    form.append("exclude", JSON.stringify(Object.fromEntries(Object.entries(off).map(([k, v]) => [k, [...v]]))));
    return start(form, "No se pudo importar el archivo; no se cambió nada");
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <button type="button" onClick={() => setOpen(true)} className="tr-btn-light px-4 py-2.5 text-sm" data-testid="import-excel-button">
        <FileSpreadsheet className="h-4 w-4" /> Subir Excel actualizado
      </button>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="import-excel-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="text-lg font-extrabold text-[#182235]">Subir Excel actualizado</DialogTitle>
          <DialogDescription className="text-xs text-[#5b667a]">
            Mismo formato que la cinta mensual (hojas "1. Loan data", "2. Borrower data" y, si las trae, "4. ExpectedPayment data" y "5. Actual Payment data"). Primero verás una revisión de todos los cambios; nada se guarda hasta pulsar "Aplicar".
          </DialogDescription>
        </DialogHeader>
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-[14px] border-2 border-dashed border-[#d7e0ec] bg-[#f8fafd] px-4 py-6 text-center hover:border-[#9bc1ff]">
          <UploadCloud className="h-7 w-7 text-[#0f5ef7]" />
          <span className="text-sm font-semibold text-[#182235]" data-testid="import-file-name">{file ? file.name : job?.file_name || "Selecciona el archivo .xlsx"}</span>
          <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="import-file-input" />
        </label>
        {file && payments === false && (
          <p className="rounded-[10px] bg-[#f5f8fc] px-3 py-2 text-xs font-semibold text-[#3b475c]" data-testid="import-no-payments-note">{MSG_NO_PAYMENTS}</p>
        )}
        {file && payments === null && !error && (
          <p className="flex items-center gap-2 text-xs text-[#5b667a]" data-testid="import-checking-sheets"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Revisando hojas del archivo…</p>
        )}
        {payments && (
        <label className="block space-y-1.5">
          <span className="text-xs font-bold text-[#3b475c]">Fecha de corte</span>
          <input
            value={cut}
            onChange={(e) => setCut(e.target.value)}
            placeholder="dd/mm/aaaa"
            inputMode="numeric"
            className="tr-input h-10 w-full text-sm"
            data-testid="import-cut-date-input"
          />
          <span className="block text-[11px] text-[#5b667a]">Fecha a la que corresponden los pagos del Excel; se usa para el saldo insoluto.</span>
        </label>
        )}
        {error && (
          <p role="alert" className="flex items-center gap-2 rounded-[10px] bg-[#fdf0ef] px-3 py-2 text-xs font-semibold text-[#b42318]" data-testid="import-error">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </p>
        )}
        {busy && <JobProgress job={job} />}
        {summary && <Summary s={summary} />}
        {review && !summary && <ImportReviewPanel p={review} off={off} toggle={toggle} />}
        {review && !summary ? (
          <div className="grid grid-cols-2 gap-2.5">
            <button type="button" onClick={cancelReview} disabled={busy} className="tr-btn-light h-11 text-sm" data-testid="import-cancel-button">Cancelar</button>
            <button type="button" onClick={sendApply} disabled={busy || !reviewId} className="tr-btn-primary h-11 text-sm" data-testid="import-apply-button">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Aplicando…</> : "Aplicar"}
            </button>
          </div>
        ) : !summary && (
          <button type="button" onClick={sendReview} disabled={busy || !file || payments === null} className="tr-btn-primary h-11 w-full text-sm" data-testid="import-submit-button">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Revisando…</> : "Revisar cambios"}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
};
